import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCredit } from "../context/CreditContext";
import Navbar from "../components/Navbar";
import { CheckCircle2, XCircle, Loader2, PackageOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 12;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshCredits } = useCredit();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking"); // checking|paid|pending|error|cancelled
  const [amount, setAmount] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setState("paid");
          refreshCredits();
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep trying */ }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled, refreshCredits]);

  return (
    <div className="min-h-screen bg-[#19142A] dark:bg-[#141924] text-white dark:text-white">
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="payment-result">
        <div className="border border-[#2A2440] dark:border-[#2A2440] bg-[#F7F8FB] dark:bg-[#141021] p-8">
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#9945FF]" />
              <h1 className="text-xl font-bold mt-4 font-cases uppercase">Confirming payment…</h1>
              <p className="text-sm text-[#8A90A0] mt-2">Hang tight, this only takes a moment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-[#14B87A]" />
              <h1 data-testid="payment-success-title" className="text-xl font-bold mt-4 font-cases uppercase">Deposit successful</h1>
              <p className="text-sm text-[#8A90A0] mt-2">
                {amount ? `$${Number(amount).toFixed(2)} credit added to your account.` : "Your credit has been added."}
              </p>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-[#9945FF]" />
              <h1 className="text-xl font-bold mt-4 font-cases uppercase">Payment processing</h1>
              <p className="text-sm text-[#8A90A0] mt-2">This is taking a little longer than usual. Your balance will update automatically once it clears.</p>
            </>
          )}
          {state === "cancelled" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-[#8A90A0]" />
              <h1 className="text-xl font-bold mt-4 font-cases uppercase">Payment cancelled</h1>
              <p className="text-sm text-[#8A90A0] mt-2">No charge was made. You can try depositing again anytime.</p>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-[#DB2777]" />
              <h1 className="text-xl font-bold mt-4 font-cases uppercase">Payment failed</h1>
              <p className="text-sm text-[#8A90A0] mt-2">Something went wrong and no credit was added. Please try again.</p>
            </>
          )}
          <Link to="/" data-testid="payment-back-btn"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg font-bold bg-[#7C4DFF] dark:bg-[#9945FF] text-white hover:opacity-90">
            <PackageOpen className="h-4 w-4" /> Back to cases
          </Link>
        </div>
      </div>
    </div>
  );
}
